import React, { useRef } from "react";
import { useDraggable, useDroppable, Overlay } from "snapdrag";
import RecipeItem from "./RecipeItem";

const GRID_COLUMNS = 5;
const GAP = 8;

// ------------------ Draggable Recipe Card ------------------
const RecipeItemDraggable = ({
  recipe,
  index,
  kind,
  onChangeStatus,
  onEditItem,
  onDelete,
}) => {
  const { draggable, isDragging } = useDraggable({
    kind,
    data: { recipe, id: recipe._id, index },
    move: true,
  });

  return draggable(
    <div
      className="border p-2 bg-white rounded-md"
      style={{ opacity: isDragging ? 0.2 : 1 }}
    >
      <RecipeItem
        item={recipe}
        onChangeStatus={onChangeStatus}
        onEditItem={() => onEditItem(recipe)}
        onDelete={() => onDelete(recipe)}
      />
    </div>
  );
};

// ------------------ Category Grid ------------------
const CategoryWithRecipes = ({
  category,
  onReorder,
  onChangeStatus,
  onEditItem,
  onDelete,
}) => {
  const kind = "RECIPE";
  const containerRef = useRef(null);

  const { droppable } = useDroppable({
    accepts: kind,
    data: { categoryId: category._id },
    onDrop: ({ data, event }) => {
      const sourceId = data.id;
      if (!containerRef.current) return;

      const newList = [...category.recipes];
      const moving = newList.find((r) => String(r._id) === String(sourceId));
      if (!moving) return;

      const gridRect = containerRef.current.getBoundingClientRect();
      const cell = containerRef.current.children[0]?.getBoundingClientRect();
      if (!cell) return;

      const cellWidth = cell.width + GAP;
      const cellHeight = cell.height + GAP;

      const col = Math.floor((event.clientX - gridRect.left) / cellWidth);
      const row = Math.floor((event.clientY - gridRect.top) / cellHeight);

      let targetIndex = row * GRID_COLUMNS + col;
      if (targetIndex < 0) targetIndex = 0;
      if (targetIndex >= newList.length) targetIndex = newList.length - 1;

      const oldIndex = newList.findIndex(
        (r) => String(r._id) === String(sourceId)
      );
      newList.splice(oldIndex, 1);
      newList.splice(targetIndex, 0, moving);

      onReorder({
        itemId: sourceId,
        categoryId: category?._id,
        orderedIds: newList.map((r) => r._id),
      });
    },
  });

  return (
    <div className="mt-8">
      <h4 className="font-medium text-lg">{category?.name ?? ""}</h4>
      <p className="font-light text-base mb-4">
        {category.recipes?.length || 0} Recipes
      </p>

      {droppable(
        <div
          ref={containerRef}
          className="grid gap-2"
          style={{
            gridTemplateColumns: `repeat(${GRID_COLUMNS}, minmax(0, 1fr))`,
          }}
        >
          {category.recipes.map((recipe, index) => (
            <RecipeItemDraggable
              key={`${recipe._id}-${index}`}
              recipe={recipe}
              index={index}
              kind={kind}
              onChangeStatus={onChangeStatus}
              onEditItem={onEditItem}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CategoryWithRecipes;
